import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  useGetAdminThreadsQuery,
  useGetAdminTagsQuery,
  usePinThreadMutation,
  useLockThreadMutation,
  useDeleteThreadMutation,
  useUpdateThreadMutation,
  useCreateAnnouncementMutation,
} from '@/store/api/corkBoard/corkBoardApi';
import { useAppSelector, useAppDispatch } from '@/store';
import { clearDeletedThreads } from '@/store/slices/residents/corkBoardUISlice';
import { useAdminUser } from './useAdminUser';
import { useToast } from '@/contexts/admin/ToastContext';
import type {
  CorkBoardThreadSummary,
  ThreadSortBy,
  CreateAnnouncementRequest,
} from '@/types/residents/corkBoard';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 30;

export const useThreadModeration = () => {
  const dispatch = useAppDispatch();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  const { showSuccess, showError } = useToast();
  const deletedThreadIds = useAppSelector((state) => state.corkBoardUI.deletedThreadIds);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<ThreadSortBy>('newest');
  const [page, setPage] = useState(0);
  const [allThreads, setAllThreads] = useState<CorkBoardThreadSummary[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchInput]);

  // Reset pagination when filters change or manual refresh is triggered
  useEffect(() => {
    setPage(0);
    setAllThreads([]);
    setHasNextPage(false);
  }, [debouncedSearch, sortBy, selectedPropertyId, refreshKey]);

  const {
    data: threadsData,
    isLoading,
    isFetching,
    error,
  } = useGetAdminThreadsQuery(
    {
      propertyId: selectedPropertyId!,
      search: debouncedSearch || undefined,
      sortBy,
      page,
      pageSize: PAGE_SIZE,
    },
    { skip: !selectedPropertyId },
  );

  const { data: tags = [] } = useGetAdminTagsQuery(
    { propertyId: selectedPropertyId!, includeDisabled: false },
    { skip: !selectedPropertyId },
  );

  // Accumulate threads across pages
  useEffect(() => {
    if (!threadsData) return;
    const announcements = threadsData.announcements ?? [];
    const regular = threadsData.threads ?? [];
    const incoming = [...announcements, ...regular];

    if (page === 0) {
      setAllThreads(incoming);
    } else {
      setAllThreads((prev) => {
        const existingIds = new Set(prev.map((t) => t.id));
        const unique = incoming.filter((t) => !existingIds.has(t.id));
        return [...prev, ...unique];
      });
    }

    const { currentPage, totalPages } = threadsData.pagination;
    setHasNextPage(currentPage < totalPages - 1);
  }, [threadsData, page]);

  const [pinThreadMutation, { isLoading: isPinning }] = usePinThreadMutation();
  const [lockThreadMutation, { isLoading: isLocking }] = useLockThreadMutation();
  const [deleteThreadMutation, { isLoading: isDeleting }] = useDeleteThreadMutation();
  const [updateThreadMutation, { isLoading: isUpdatingTags }] = useUpdateThreadMutation();
  const [createAnnouncementMutation, { isLoading: isCreatingAnnouncement }] = useCreateAnnouncementMutation();

  const isMutating = isPinning || isLocking || isDeleting || isUpdatingTags || isCreatingAnnouncement;
  const isFetchingNextPage = isFetching && page > 0;

  const visibleThreads = useMemo(
    () => deletedThreadIds.length > 0
      ? allThreads.filter((t) => !deletedThreadIds.includes(String(t.id)))
      : allThreads,
    [allThreads, deletedThreadIds],
  );

  const loadMore = useCallback(() => {
    if (!isFetching && hasNextPage) {
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasNextPage]);

  const handleRefetch = useCallback(() => {
    dispatch(clearDeletedThreads());
    setRefreshKey((k) => k + 1);
  }, [dispatch]);

  const totalCount = threadsData?.pagination?.totalCount ?? 0;

  const handlePinThread = useCallback(
    async (thread: CorkBoardThreadSummary) => {
      try {
        await pinThreadMutation({ threadId: thread.id, isPinned: !thread.isPinned }).unwrap();
        showSuccess(thread.isPinned ? 'Thread unpinned' : 'Thread pinned');
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to update pin status');
        return false;
      }
    },
    [pinThreadMutation, showSuccess, showError],
  );

  const handleLockThread = useCallback(
    async (thread: CorkBoardThreadSummary) => {
      try {
        await lockThreadMutation({ threadId: thread.id, isLocked: !thread.isLocked }).unwrap();
        showSuccess(thread.isLocked ? 'Thread unlocked' : 'Thread locked');
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to update lock status');
        return false;
      }
    },
    [lockThreadMutation, showSuccess, showError],
  );

  const handleDeleteThread = useCallback(
    async (threadId: string) => {
      try {
        await deleteThreadMutation(threadId).unwrap();
        showSuccess('Thread deleted');
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to delete thread');
        return false;
      }
    },
    [deleteThreadMutation, showSuccess, showError],
  );

  const handleUpdateThreadTags = useCallback(
    async (threadId: string, tagIds: number[]) => {
      try {
        await updateThreadMutation({ threadId, tagIds }).unwrap();
        showSuccess('Thread tags updated');
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to update thread tags');
        return false;
      }
    },
    [updateThreadMutation, showSuccess, showError],
  );

  const handleCreateAnnouncement = useCallback(
    async (data: Omit<CreateAnnouncementRequest, 'propertyId'>) => {
      if (!selectedPropertyId) return false;
      try {
        await createAnnouncementMutation({ ...data, propertyId: selectedPropertyId }).unwrap();
        showSuccess('Announcement created');
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to create announcement');
        return false;
      }
    },
    [selectedPropertyId, createAnnouncementMutation, showSuccess, showError],
  );

  return {
    threads: visibleThreads,
    tags,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isMutating,
    isPinning,
    isLocking,
    isDeleting,
    isUpdatingTags,
    isCreatingAnnouncement,
    error,
    selectedPropertyId,
    searchInput,
    setSearchInput,
    sortBy,
    setSortBy,
    refetch: handleRefetch,
    hasNextPage,
    loadMore,
    totalCount,
    handlePinThread,
    handleLockThread,
    handleDeleteThread,
    handleUpdateThreadTags,
    handleCreateAnnouncement,
  };
};
